import {
  Component,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
  Input,
  NgZone,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import * as faceapi from 'face-api.js';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { ToastService } from '../../services/toast/toast.service';
import { TranslateService } from '@ngx-translate/core';

interface PhoneNumber {
  key: string;
  value: string;
}

interface FaceDetectionResponse {
  boundingBox?: {
    live?: boolean;
    confidence_score?: number;
    boundingBox?: number[][];
    status?: boolean;
    message?: string;
  };
  status?: boolean;
  data?: any;
}

@Component({
  selector: 'irembogov-irembo-selfie-verification',
  templateUrl: './irembo-selfie-verification.component.html',
  styleUrls: ['./irembo-selfie-verification.component.scss'],
})
export class IremboSelfieVerificationComponent implements OnInit, OnDestroy {
  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasElement') canvasElement!: ElementRef<HTMLCanvasElement>;

  @Input() cameraNotAvailableErrorMessage!: string;
  @Input() verificationErrorMessage!: string;
  @Input() verificationCodeSentSuccessMessage!: string;
  @Input() phoneNumberSelectionTitle!: string;
  @Input() citizenPhotoNotAvailableMessage!: string;
  @Input() phoneNumberRequiredMessage!: string;
  @Input() parentComponent: any;
  @Input() customErrorMessages: { [key: string]: string } = {};

  @Input() maxAttempts = 3;
  @Input() preEnrollmentTempId: string | null = null;
  @Input() legalGuardianId: string | null = null;
  @Input() phoneConfig: any;

  // New face detection endpoint inputs
  @Input() useDetectFaceEndpoint = false;
  @Input() detectFaceEndpoint: string | null = null;

  private stream: MediaStream | null = null;
  private readonly facingMode = 'user';
  private animationFrameId: number | null = null;
  private detectionRunning = false;
  private lastDetectionTime = 0;
  private readonly detectionThrottleMs = 50;
  private stableFaceCount = 0;
  private readonly requiredStableFaces = 2;

  // Static properties to cache models across all component instances
  private static modelsLoaded = false;
  private static modelLoadPromise: Promise<void> | null = null;

  // State variables
  isReady = false;
  faceDetected = false;
  statusMessage = 'Position your face within the frame';
  cameraAvailable = true;
  isFrozen = false;
  isVerifying = false;
  verificationFailed = false;
  verificationSuccess = false;
  capturedImage: string | null = null;
  verificationErrorDetails!: string;

  // Multi-step verification state
  verificationSteps = {
    left: { completed: false, detecting: false },
    right: { completed: false, detecting: false },
    center: { completed: false, detecting: false },
  };
  currentStep: 'left' | 'right' | 'center' = 'left';
  allStepsCompleted = false;
  private stepConfirmationCount = 0;
  private readonly requiredStepConfirmations = 8;

  // Anti-spoofing: Track face loss to prevent phone substitution
  private consecutiveFaceLossCount = 0;
  private readonly maxAllowedFaceLoss = 15;

  // Lock detection once step completes to keep green state
  private stepCompletionLocked = false;

  // Phone verification
  showPhoneVerification = false;
  citizenPhotoNotAvailableError = false;
  phoneNumberRequiredError = false;
  displayedPhoneVerificationMessage = '';
  @Input() phoneNumbers: PhoneNumber[] = [];
  showManualInput = false;
  phoneFormControl = new FormControl('', [
    Validators.required,
    Validators.pattern('\\+2507\\d{8}'),
  ]);
  attemptsFromApi = 0;

  phoneSelectControl = new FormControl();
  formattedPhoneNumbers: Array<{ label: string; value: string; key: string }> =
    [];
  selectedPhoneKey: string | null = null;

  showSuccessScreen = false;
  verificationRequestInProgress = false;
  codeWasSent = false;

  private formatPhoneNumbersForDropdown() {
    this.formattedPhoneNumbers = this.phoneNumbers?.map(phone => ({
      label: phone.value,
      value: phone.value,
      key: phone.key,
    }));

    if (this.formattedPhoneNumbers.length > 0) {
      this.formattedPhoneNumbers.push({
        label: this.translateService.instant('Enter manually'),
        value: 'manual',
        key: 'manual',
      });
    }
  }

  constructor(
    public activeModal: NgbActiveModal,
    private toastService: ToastService,
    private translateService: TranslateService,
    private readonly cd: ChangeDetectorRef,
    private zone: NgZone
  ) {}

  private async initializeCamera() {
    try {
      // Ensure camera is fully stopped before restarting
      this.stopCamera();

      // Reset state variables
      this.faceDetected = false;
      this.stableFaceCount = 0;
      this.isFrozen = false;
      this.detectionRunning = false;
      this.lastDetectionTime = 0;
      this.resetVerificationSteps();

      try {
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: this.facingMode,
            width: { ideal: 640 },
            height: { ideal: 480 },
          },
          audio: false,
        });
      } catch (e) {
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
      }

      if (this.videoElement?.nativeElement) {
        this.videoElement.nativeElement.srcObject = this.stream;

        this.videoElement.nativeElement.onloadedmetadata = () => {
          this.videoElement.nativeElement.play();
          this.cameraAvailable = true;

          if (IremboSelfieVerificationComponent.modelsLoaded) {
            this.isReady = true;
            this.startFaceDetection();
          } else {
            this.loadFaceDetectionModels();
          }

          this.cd.detectChanges();
        };
      }
    } catch (err) {
      this.handleCameraError();
    }
  }

  private handleCameraError() {
    this.stopCamera();
    this.cameraAvailable = false;
    this.statusMessage = 'Unable to access camera';
    this.cd.detectChanges();
  }

  private stopCamera() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    if (this.videoElement?.nativeElement) {
      this.videoElement.nativeElement.srcObject = null;
    }

    this.detectionRunning = false;
  }

  ngOnInit() {
    this.phoneFormControl.setValidators([
      Validators.required,
      Validators.pattern(this.phoneConfig?.pattern || '\\+2507\\d{8}'),
    ]);

    this.formatPhoneNumbersForDropdown();
    this.preloadFaceDetectionModels();
    this.initializeCamera();
  }

  private preloadFaceDetectionModels() {
    if (
      !IremboSelfieVerificationComponent.modelsLoaded &&
      !IremboSelfieVerificationComponent.modelLoadPromise
    ) {
      IremboSelfieVerificationComponent.modelLoadPromise = Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri('/assets/models'),
        faceapi.nets.faceLandmark68Net.loadFromUri('/assets/models'),
      ]).then(() => {
        console.log('Face detection models loaded successfully');
        IremboSelfieVerificationComponent.modelsLoaded = true;
      });
    }
  }

  private async loadFaceDetectionModels() {
    try {
      if (IremboSelfieVerificationComponent.modelsLoaded) {
        this.isReady = true;
        this.startFaceDetection();
        return;
      }

      if (IremboSelfieVerificationComponent.modelLoadPromise) {
        await IremboSelfieVerificationComponent.modelLoadPromise;
        this.isReady = true;
        this.startFaceDetection();
        return;
      }

      this.preloadFaceDetectionModels();
      await IremboSelfieVerificationComponent.modelLoadPromise;
      this.isReady = true;

      if (this.stream && !this.isFrozen && !this.isVerifying) {
        this.startFaceDetection();
      }
    } catch (err: any) {
      console.error('Error loading face detection models:', err);
      this.verificationErrorDetails = err.toString();
    }

    this.cd.detectChanges();
  }

  private startFaceDetection() {
    this.stableFaceCount = 0;
    this.faceDetected = false;
    this.detectionRunning = false;
    this.lastDetectionTime = 0;
    this.detectFace();
  }

  private detectFace() {
    // Stop detection if step is locked (completed and waiting)
    if (
      !this.videoElement?.nativeElement ||
      this.isFrozen ||
      this.isVerifying ||
      !this.isReady ||
      this.detectionRunning ||
      !this.stream ||
      this.stepCompletionLocked
    ) {
      this.scheduleNextDetection();
      return;
    }

    if (
      !this.videoElement.nativeElement.videoWidth ||
      !this.videoElement.nativeElement.videoHeight
    ) {
      this.scheduleNextDetection();
      return;
    }

    const now = Date.now();
    if (now - this.lastDetectionTime < this.detectionThrottleMs) {
      this.scheduleNextDetection();
      return;
    }

    this.lastDetectionTime = now;
    this.detectionRunning = true;

    this.zone.runOutsideAngular(async () => {
      try {
        if (
          !this.videoElement?.nativeElement ||
          !this.videoElement.nativeElement.videoWidth ||
          !this.videoElement.nativeElement.videoHeight
        ) {
          this.zone.run(() => {
            this.detectionRunning = false;
            this.scheduleNextDetection();
          });
          return;
        }

        const options = new faceapi.TinyFaceDetectorOptions({
          inputSize: 320,
          scoreThreshold: 0.5,
        });

        const detections = await faceapi
          .detectSingleFace(this.videoElement.nativeElement, options)
          .withFaceLandmarks();

        this.zone.run(() => {
          if (detections) {
            this.processFaceDetection(detections);
          } else {
            // No face detected - handle face loss
            this.handleFaceLoss();
          }

          this.detectionRunning = false;
          this.scheduleNextDetection();
        });
      } catch (error) {
        this.zone.run(() => {
          this.detectionRunning = false;
          this.scheduleNextDetection();
        });
      }
    });
  }

  private processFaceDetection(
    detection: faceapi.WithFaceLandmarks<{ detection: faceapi.FaceDetection }>
  ) {
    this.consecutiveFaceLossCount = 0;

    const orientation = this.determineFaceOrientation(detection);

    if (orientation === this.currentStep) {
      this.stepConfirmationCount++;

      if (!this.verificationSteps[this.currentStep].completed) {
        this.verificationSteps[this.currentStep].detecting = true;
      }

      if (this.stepConfirmationCount >= this.requiredStepConfirmations) {
        // Step completed!
        this.completeCurrentStep();
      }
    } else {
      this.stepConfirmationCount = 0;
      if (!this.verificationSteps[this.currentStep].completed) {
        this.verificationSteps[this.currentStep].detecting = false;
      }
    }

    if (!this.faceDetected && orientation !== null) {
      this.faceDetected = true;
      this.cd.detectChanges();
    }
  }

  private handleFaceLoss() {
    if (this.allStepsCompleted || this.stepCompletionLocked) {
      return;
    }

    this.stableFaceCount = 0;

    if (this.faceDetected) {
      this.faceDetected = false;
      this.cd.detectChanges();
    }

    // Anti-spoofing: Check if we're on center step with previous steps completed
    if (
      this.currentStep === 'center' &&
      (this.verificationSteps.left.completed ||
        this.verificationSteps.right.completed)
    ) {
      this.consecutiveFaceLossCount++;

      if (this.consecutiveFaceLossCount >= this.maxAllowedFaceLoss) {
        console.warn(
          'Anti-spoofing: Face left frame during center step - resetting verification'
        );
        this.resetAllSteps();
      }
    } else {
      // Reset counter for other steps
      this.consecutiveFaceLossCount = 0;
    }

    // Reset step detection state
    this.stepConfirmationCount = 0;
    this.verificationSteps[this.currentStep].detecting = false;
  }

  private resetAllSteps() {
    // Reset all verification state
    this.verificationSteps = {
      left: { completed: false, detecting: false },
      right: { completed: false, detecting: false },
      center: { completed: false, detecting: false },
    };
    this.currentStep = 'left';
    this.allStepsCompleted = false;
    this.stepConfirmationCount = 0;
    this.consecutiveFaceLossCount = 0;
    this.faceDetected = false;
    this.stepCompletionLocked = false;

    this.cd.detectChanges();
  }

  private determineFaceOrientation(
    detection: faceapi.WithFaceLandmarks<{ detection: faceapi.FaceDetection }>
  ): 'left' | 'right' | 'center' | null {
    try {
      const landmarks = detection.landmarks;
      const leftEye = landmarks.getLeftEye();
      const rightEye = landmarks.getRightEye();
      const nose = landmarks.getNose();
      const jawOutline = landmarks.getJawOutline();

      const faceBox = detection.detection.box;
      const videoWidth = this.videoElement?.nativeElement?.videoWidth || 640;
      const videoHeight = this.videoElement?.nativeElement?.videoHeight || 480;

      // Calculate eye centers
      const leftEyeCenter = {
        x: leftEye.reduce((sum, point) => sum + point.x, 0) / leftEye.length,
        y: leftEye.reduce((sum, point) => sum + point.y, 0) / leftEye.length,
      };

      const rightEyeCenter = {
        x: rightEye.reduce((sum, point) => sum + point.x, 0) / rightEye.length,
        y: rightEye.reduce((sum, point) => sum + point.y, 0) / rightEye.length,
      };

      const noseTip = nose[6];
      const faceCenter = faceBox.x + faceBox.width / 2;

      // Calculate horizontal offset of nose from face center
      const noseOffset = noseTip.x - faceCenter;
      const normalizedOffset = noseOffset / faceBox.width;

      // Calculate eye visibility (for profile detection)
      const eyeDistance = Math.abs(rightEyeCenter.x - leftEyeCenter.x);
      const normalizedEyeDistance = eyeDistance / faceBox.width;

      // Check if face is centered in frame
      const isCenteredInFrame =
        Math.abs(faceCenter - videoWidth / 2) < videoWidth * 0.25 &&
        Math.abs(faceBox.y + faceBox.height / 2 - videoHeight / 2) <
          videoHeight * 0.25;

      if (!isCenteredInFrame) {
        return null; // Face not in good position
      }

      // Calculate eye angle for head tilt detection
      const eyeAngle = Math.abs(
        Math.atan2(
          rightEyeCenter.y - leftEyeCenter.y,
          rightEyeCenter.x - leftEyeCenter.x
        ) *
          (180 / Math.PI)
      );

      if (
        Math.abs(normalizedOffset) < 0.28 &&
        normalizedEyeDistance > 0.3 &&
        eyeAngle < 35
      ) {
        return 'center';
      }

      if (normalizedOffset > 0.15 && normalizedEyeDistance < 0.45) {
        return 'left';
      }

      if (normalizedOffset < -0.15 && normalizedEyeDistance < 0.45) {
        return 'right';
      }

      return null;
    } catch (error) {
      return null;
    }
  }

  private completeCurrentStep() {
    this.verificationSteps[this.currentStep].completed = true;
    this.stepConfirmationCount = 0;

    // Move to next step
    if (this.currentStep === 'left') {
      this.verificationSteps.left.detecting = false;
      this.currentStep = 'right';
      this.cd.detectChanges();
    } else if (this.currentStep === 'right') {
      this.verificationSteps.right.detecting = false;
      this.currentStep = 'center';
      this.cd.detectChanges();
    } else if (this.currentStep === 'center') {
      this.verificationSteps.center.detecting = true;
      this.allStepsCompleted = true;
      this.stepCompletionLocked = true;
      this.cd.detectChanges();

      setTimeout(() => {
        this.cd.detectChanges();

        // Auto-capture after brief hold
        setTimeout(() => {
          this.takePicture();
        }, 300);
      }, 700); // Reduced from 1000ms for faster flow
    }
  }

  private resetVerificationSteps() {
    this.verificationSteps = {
      left: { completed: false, detecting: false },
      right: { completed: false, detecting: false },
      center: { completed: false, detecting: false },
    };
    this.currentStep = 'left';
    this.allStepsCompleted = false;
    this.stepConfirmationCount = 0;
    this.consecutiveFaceLossCount = 0;
    this.stepCompletionLocked = false;
  }

  getStepInstructions(): string {
    if (this.allStepsCompleted && this.isFrozen) {
      return 'Perfect! Capturing image and validating face...';
    }

    if (this.allStepsCompleted) {
      return 'Perfect! Hold still...';
    }

    switch (this.currentStep) {
      case 'left':
        return 'Please turn your face to the left';
      case 'right':
        return 'Please turn your face to the right';
      case 'center':
        return 'Please look straight at the camera';
      default:
        return 'Position your face within the frame';
    }
  }

  private scheduleNextDetection() {
    if (
      this.stream &&
      !this.isFrozen &&
      !this.isVerifying &&
      !this.stepCompletionLocked
    ) {
      if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
      }
      this.animationFrameId = requestAnimationFrame(() => this.detectFace());
    }
  }

  private checkFaceOrientation(
    detection: faceapi.WithFaceLandmarks<{ detection: faceapi.FaceDetection }>
  ): boolean {
    try {
      const landmarks = detection.landmarks;
      const nose = landmarks.getNose();
      const leftEye = landmarks.getLeftEye();
      const rightEye = landmarks.getRightEye();
      const jawOutline = landmarks.getJawOutline();

      const faceBox = detection.detection.box;
      const centerX = faceBox.x + faceBox.width / 2;
      const centerY = faceBox.y + faceBox.height / 2;

      if (!this.videoElement?.nativeElement) return false;

      const videoWidth = this.videoElement.nativeElement.videoWidth;
      const videoHeight = this.videoElement.nativeElement.videoHeight;

      const isCentered =
        Math.abs(centerX - videoWidth / 2) < videoWidth * 0.3 &&
        Math.abs(centerY - videoHeight / 2) < videoHeight * 0.3;

      if (!isCentered) return false;

      const leftEyeCenter = {
        x: leftEye.reduce((sum, point) => sum + point.x, 0) / leftEye.length,
        y: leftEye.reduce((sum, point) => sum + point.y, 0) / leftEye.length,
      };

      const rightEyeCenter = {
        x: rightEye.reduce((sum, point) => sum + point.x, 0) / rightEye.length,
        y: rightEye.reduce((sum, point) => sum + point.y, 0) / rightEye.length,
      };

      const eyeAngle = Math.abs(
        Math.atan2(
          rightEyeCenter.y - leftEyeCenter.y,
          rightEyeCenter.x - leftEyeCenter.x
        ) *
          (180 / Math.PI)
      );

      if (eyeAngle > 25) return false;

      const noseTop = nose[0];
      const noseTip = nose[6];

      const noseAngle = Math.abs(
        Math.atan2(noseTip.x - noseTop.x, noseTip.y - noseTop.y) *
          (180 / Math.PI)
      );

      if (noseAngle > 25) return false;

      const jawLeft = jawOutline[0];
      const jawRight = jawOutline[jawOutline.length - 1];

      const jawLeftDist = Math.sqrt(
        Math.pow(noseTip.x - jawLeft.x, 2) + Math.pow(noseTip.y - jawLeft.y, 2)
      );

      const jawRightDist = Math.sqrt(
        Math.pow(noseTip.x - jawRight.x, 2) +
          Math.pow(noseTip.y - jawRight.y, 2)
      );

      const symmetryRatio =
        Math.min(jawLeftDist, jawRightDist) /
        Math.max(jawLeftDist, jawRightDist);

      if (symmetryRatio < 0.7) return false;

      const faceArea = faceBox.width * faceBox.height;
      const videoArea = videoWidth * videoHeight;
      const faceRatio = faceArea / videoArea;

      if (faceRatio < 0.07 || faceRatio > 0.5) return false;

      return true;
    } catch (error) {
      return false;
    }
  }

  async takePicture() {
    if (!this.videoElement?.nativeElement || !this.allStepsCompleted) return;

    try {
      this.isFrozen = true;
      const video = this.videoElement.nativeElement;

      const fullCanvas = document.createElement('canvas');
      fullCanvas.width = video.videoWidth;
      fullCanvas.height = video.videoHeight;
      const ctx = fullCanvas.getContext('2d');

      if (!ctx) {
        this.isFrozen = false;
        return;
      }

      ctx.translate(fullCanvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
      ctx.setTransform(1, 0, 0, 1, 0, 0);

      this.capturedImage = fullCanvas.toDataURL('image/jpeg', 0.7);

      if (this.useDetectFaceEndpoint && this.detectFaceEndpoint) {
        await this.processWithExternalDetection(fullCanvas);
      } else {
        await this.processWithFullImage(fullCanvas);
      }
    } catch (error) {
      console.error('Error taking picture:', error);
      this.isFrozen = false;
      await this.initializeCamera();
    }
  }

  private async processWithExternalDetection(fullCanvas: HTMLCanvasElement) {
    try {
      this.isVerifying = true;
      this.stopCamera();
      this.cd.detectChanges();

      const fullImageBase64 = fullCanvas.toDataURL('image/jpeg', 0.8);
      const detectionResult = await this.detectFaceWithEndpoint(
        fullImageBase64
      );

      if (detectionResult) {
        const faceImage = await this.extractFaceFromBoundingBox(
          fullCanvas,
          detectionResult
        );

        if (faceImage) {
          this.capturedImage = faceImage;
          setTimeout(() => {
            this.startVerification(faceImage);
          }, 500);
        } else {
          throw new Error('Failed to extract face from bounding box');
        }
      } else {
        throw new Error('Face detection failed');
      }
    } catch (error) {
      console.error('External face detection failed:', error);
      this.handleFaceDetectionError();
    }
  }

  private async processWithFullImage(fullCanvas: HTMLCanvasElement) {
    console.log('Processing with full image - no cropping applied');

    const maxDimension = 800;
    const aspectRatio = fullCanvas.width / fullCanvas.height;
    let targetWidth, targetHeight;

    if (fullCanvas.width > fullCanvas.height) {
      targetWidth = Math.min(maxDimension, fullCanvas.width);
      targetHeight = Math.round(targetWidth / aspectRatio);
    } else {
      targetHeight = Math.min(maxDimension, fullCanvas.height);
      targetWidth = Math.round(targetHeight * aspectRatio);
    }

    const resizedFullImage = await this.resizeImage(
      fullCanvas,
      targetWidth,
      targetHeight,
      0.85
    );

    this.capturedImage = resizedFullImage;
    this.stopCamera();
    this.cd.detectChanges();

    setTimeout(() => {
      this.startVerification(resizedFullImage);
    }, 500);
  }

  private async detectFaceWithEndpoint(base64Image: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.parentComponent || !this.detectFaceEndpoint) {
        reject(
          new Error('Parent component or detection endpoint not available')
        );
        return;
      }

      this.parentComponent
        .submitFaceDetection({
          selfie: base64Image,
        })
        .pipe(
          finalize(() => {
            // Any cleanup if needed
          })
        )
        .subscribe(
          (response: any) => {
            console.log('Raw response from face detection endpoint:', response);

            if (response?.status === true && response?.data?.response) {
              try {
                const parsedResponse = JSON.parse(response.data.response);
                console.log('Parsed response:', parsedResponse);

                if (
                  parsedResponse?.boundingBox?.status === true &&
                  parsedResponse?.boundingBox?.boundingBox
                ) {
                  resolve(parsedResponse.boundingBox);
                } else {
                  reject(
                    new Error(
                      parsedResponse?.boundingBox?.message ||
                        'Face detection failed'
                    )
                  );
                }
              } catch (parseError) {
                console.error('Error parsing response:', parseError);
                reject(
                  new Error(
                    'Invalid response format from face detection endpoint'
                  )
                );
              }
            } else {
              reject(
                new Error(response?.responseMessage || 'Face detection failed')
              );
            }
          },
          (error: HttpErrorResponse) => {
            const errorMessage =
              error?.error?.responseMessage || 'Face detection endpoint failed';
            reject(new Error(errorMessage));
          }
        );
    });
  }

  private async extractFaceFromBoundingBox(
    canvas: HTMLCanvasElement,
    boundingBoxData: any
  ): Promise<string | null> {
    try {
      const boundingBox = boundingBoxData.boundingBox?.[0];

      if (!boundingBox || boundingBox.length !== 4) {
        console.error('Invalid bounding box format:', boundingBox);
        return null;
      }

      const [x1, y1, x2, y2] = boundingBox;
      const x = Math.round(x1);
      const y = Math.round(y1);
      const width = Math.round(x2 - x1);
      const height = Math.round(y2 - y1);

      console.log('Bounding box coordinates:', { x1, y1, x2, y2 });
      console.log('Calculated crop area:', { x, y, width, height });

      if (
        x < 0 ||
        y < 0 ||
        width <= 0 ||
        height <= 0 ||
        x + width > canvas.width ||
        y + height > canvas.height
      ) {
        console.error('Invalid bounding box coordinates:', {
          x,
          y,
          width,
          height,
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
        });
        return null;
      }

      const faceCanvas = document.createElement('canvas');
      faceCanvas.width = width;
      faceCanvas.height = height;
      const faceCtx = faceCanvas.getContext('2d');

      if (!faceCtx) {
        return null;
      }

      faceCtx.drawImage(canvas, x, y, width, height, 0, 0, width, height);

      const targetSize = Math.min(400, Math.max(width, height));
      const aspectRatio = width / height;
      let targetWidth, targetHeight;

      if (aspectRatio > 1) {
        targetWidth = targetSize;
        targetHeight = Math.round(targetSize / aspectRatio);
      } else {
        targetHeight = targetSize;
        targetWidth = Math.round(targetSize * aspectRatio);
      }

      console.log('Resizing face to:', { targetWidth, targetHeight });

      return await this.resizeImage(
        faceCanvas,
        targetWidth,
        targetHeight,
        0.85
      );
    } catch (error) {
      console.error('Error extracting face from bounding box:', error);
      return null;
    }
  }

  private handleFaceDetectionError() {
    this.isFrozen = false;
    this.isVerifying = false;
    this.verificationFailed = true;
    this.verificationErrorDetails =
      'Face detection failed. Please ensure your face is clearly visible and try again.';
    this.cd.detectChanges();
  }

  private resizeImage(
    sourceCanvas: HTMLCanvasElement,
    targetWidth: number,
    targetHeight: number,
    quality: any = 0.6
  ): Promise<string> {
    return new Promise(resolve => {
      const resizeCanvas = document.createElement('canvas');
      resizeCanvas.width = targetWidth;
      resizeCanvas.height = targetHeight;
      const resizeCtx = resizeCanvas.getContext('2d');

      if (resizeCtx) {
        resizeCtx.imageSmoothingEnabled = true;
        resizeCtx.imageSmoothingQuality = 'high';

        resizeCtx.drawImage(
          sourceCanvas,
          0,
          0,
          sourceCanvas.width,
          sourceCanvas.height,
          0,
          0,
          targetWidth,
          targetHeight
        );

        const resizedImage = resizeCanvas.toDataURL('image/jpeg', quality);
        resolve(resizedImage);
      } else {
        resolve(sourceCanvas.toDataURL('image/jpeg', quality));
      }
    });
  }

  async startVerification(base64Image: string) {
    const idToUse = this.preEnrollmentTempId ?? this.legalGuardianId;
    if (!idToUse || !this.parentComponent) {
      this.isVerifying = false;
      this.verificationFailed = true;
      this.verificationErrorDetails =
        'Missing required data for verification. Please try again.';
      this.cd.detectChanges();
      return;
    }

    try {
      this.isVerifying = true;
      this.cd.detectChanges();

      this.parentComponent
        .submitVerification({
          selfie: base64Image,
        })
        .pipe(
          finalize(() => {
            this.isVerifying = false;
            this.cd.detectChanges();
          })
        )
        .subscribe(
          (response: any) => {
            if (!response || response.status !== true || !response.data) {
              this.handleErrorResponse(response);
              return;
            }

            try {
              if (!response.data.response) {
                this.handleVerificationSuccess();
                return;
              }

              const responseObj = this.parseResponseData(
                response.data.response
              );
              if (!responseObj) {
                this.handleVerificationError({
                  message: 'Invalid response format',
                });
                return;
              }

              if (this.isSkipRequestSuccess(responseObj)) {
                this.handleSkipRequestSuccess();
                return;
              }

              this.handleVerificationSuccess();
            } catch (e) {
              this.handleVerificationError({
                message: 'Error processing response',
              });
            }
          },
          (error: HttpErrorResponse) => {
            this.handleErrorFromAPI(error);
          }
        );
    } catch (error) {
      this.handleVerificationError();
    }
  }

  private parseResponseData(responseData: string) {
    return JSON.parse(responseData);
  }

  private isSkipRequestSuccess(responseObj: any): boolean {
    return responseObj?.message?.includes(
      'Face verification skip request sent successfully'
    );
  }

  private handleSkipRequestSuccess(): void {
    this.codeWasSent = true;
    this.showSuccessScreen = true;
    this.cd.detectChanges();
  }

  private handleVerificationSuccess(): void {
    this.verificationSuccess = true;
    this.cd.detectChanges();
  }

  private handleErrorResponse(response: any): void {
    this.handleVerificationError({
      message: response?.responseMessage || 'Verification unsuccessful',
    });
  }

  private handleErrorFromAPI(error: HttpErrorResponse) {
    this.isVerifying = false;
    this.verificationFailed = true;

    try {
      this.extractAndProcessErrorDetails(error);
    } catch (unexpectedError) {
      this.verificationErrorDetails =
        'Verification failed. Please ensure your face is clearly visible.';
    }

    this.checkForSpecialErrorCases();
  }

  private extractAndProcessErrorDetails(error: HttpErrorResponse): void {
    if (!error.error) {
      this.verificationErrorDetails =
        error.message || 'Verification failed. Please try again.';
      return;
    }

    if (!error.error.responseMessage) {
      this.verificationErrorDetails =
        error.error.message ||
        error.message ||
        'Verification failed. Please try again.';
      return;
    }

    this.processResponseMessage(error.error.responseMessage);
  }

  private processResponseMessage(responseMessageString: string): void {
    let responseMessageObj;

    try {
      responseMessageObj = JSON.parse(responseMessageString);
    } catch (parseError) {
      this.verificationErrorDetails = responseMessageString;
      this.checkForSpecialErrorCases();
      return;
    }

    if (!responseMessageObj.message) {
      this.setErrorDetailsFromResponseObj(responseMessageObj);
      this.checkForSpecialErrorCases();
      return;
    }

    this.processErrorMessage(responseMessageObj.message);
  }

  private processErrorMessage(messageString: string): void {
    const attemptsRegex = /attempts\s*:\s*(\d+)/i;
    const attemptsMatch = attemptsRegex.exec(messageString);

    if (attemptsMatch?.[1]) {
      const parsedAttempts = parseInt(attemptsMatch[1], 10);
      if (!Number.isNaN(parsedAttempts)) {
        this.attemptsFromApi = parsedAttempts;
        if (this.attemptsFromApi >= 2) {
          this.showPhoneVerification = true;
          setTimeout(() => {
            this.cd.detectChanges();
          }, 0);
        }
      }
    }

    try {
      const errorDetailsObj = JSON.parse(messageString);
      this.processErrorDetails(errorDetailsObj);
    } catch (error) {
      this.verificationErrorDetails = messageString;
      console.error('Error parsing error message:', error);
    }

    this.checkForSpecialErrorCases();
  }

  private processErrorDetails(errorDetailsObj: any): void {
    if (errorDetailsObj.errorDescription) {
      this.verificationErrorDetails = errorDetailsObj.errorDescription;
    }

    this.processAttemptsCount(errorDetailsObj.attempts);
  }

  private processAttemptsCount(attempts: number | undefined): void {
    if (attempts !== undefined) {
      this.attemptsFromApi = attempts;

      if (this.attemptsFromApi >= 2) {
        this.showPhoneVerification = true;
        setTimeout(() => this.cd.detectChanges(), 0);
      }
    }
  }

  private setErrorDetailsFromResponseObj(responseMessageObj: any): void {
    this.verificationErrorDetails = responseMessageObj.statusCode
      ? `Error ${responseMessageObj.statusCode}: ${
          responseMessageObj.path || 'Unknown path'
        }`
      : 'Verification failed. Please try again.';
  }

  private handleVerificationError(error?: any) {
    this.isVerifying = false;
    this.verificationFailed = true;

    if (error?.message) {
      this.verificationErrorDetails = error.message;
    } else if (error?.error?.responseMessage) {
      this.verificationErrorDetails = error.error.responseMessage;
    } else {
      this.verificationErrorDetails =
        'Verification failed. Please ensure your face is clearly visible.';
    }

    this.checkForSpecialErrorCases();
    this.cd.detectChanges();
  }

  private checkForSpecialErrorCases(): void {
    if (!this.verificationErrorDetails) {
      return;
    }

    const errorMessage = this.verificationErrorDetails.toLowerCase();

    const isCitizenPhotoNotAvailable =
      errorMessage.includes('citizen photo is not available') ||
      errorMessage.includes('citizen photo not available');

    const isPhoneNumberRequired = errorMessage.includes(
      'phone number is required for skip selfie verification'
    );

    if (isCitizenPhotoNotAvailable) {
      this.citizenPhotoNotAvailableError = true;
      this.phoneNumberRequiredError = false;
      this.showPhoneVerification = true;

      if (this.citizenPhotoNotAvailableMessage) {
        this.displayedPhoneVerificationMessage = this.translateService.instant(
          this.citizenPhotoNotAvailableMessage
        );
      } else {
        this.displayedPhoneVerificationMessage = this.verificationErrorDetails;
      }

      setTimeout(() => {
        this.cd.detectChanges();
      }, 0);
    } else if (isPhoneNumberRequired) {
      this.phoneNumberRequiredError = true;
      this.citizenPhotoNotAvailableError = false;
      this.showPhoneVerification = true;

      if (this.phoneNumberRequiredMessage) {
        this.displayedPhoneVerificationMessage = this.translateService.instant(
          this.phoneNumberRequiredMessage
        );
      } else {
        this.displayedPhoneVerificationMessage = this.verificationErrorDetails;
      }

      setTimeout(() => {
        this.cd.detectChanges();
      }, 0);
    }
  }

  private getCustomErrorMessage(errorMessage: string): string | null {
    if (
      !this.customErrorMessages ||
      Object.keys(this.customErrorMessages).length === 0
    ) {
      return null;
    }

    for (const [errorKey, customMessageKey] of Object.entries(
      this.customErrorMessages
    )) {
      if (errorMessage.toLowerCase().includes(errorKey.toLowerCase())) {
        return this.translateService.instant(customMessageKey);
      }
    }

    return null;
  }

  getDisplayErrorMessage(): string {
    if (!this.verificationErrorDetails) {
      return 'Something went wrong. Please try again or contact our team if the issue persists.';
    }

    const customMessage = this.getCustomErrorMessage(
      this.verificationErrorDetails
    );
    if (customMessage) {
      return customMessage;
    }

    if (this.verificationErrorDetails.toLowerCase().includes('match')) {
      return 'Face verification unsuccessful. Please make sure your face is clearly visible and properly positioned in front of the camera.';
    }

    return 'Something went wrong. Please try again or contact our team if the issue persists.';
  }

  onPhoneNumberSelected(value: string) {
    if (value === 'manual') {
      this.showManualInput = true;
      this.phoneFormControl.enable();
      this.phoneFormControl.reset();
      this.selectedPhoneKey = null;
    } else {
      this.showManualInput = false;
      this.phoneFormControl.setValue(value);
      this.phoneFormControl.disable();

      const selectedPhone = this.formattedPhoneNumbers.find(
        p => p.value === value
      );
      this.selectedPhoneKey = selectedPhone?.key ?? null;
    }
  }

  continueWithSuccess() {
    this.activeModal.close({
      status: 'verification_successful',
      selfie: this.capturedImage,
    });
  }

  async retryVerification() {
    this.isFrozen = false;
    this.isVerifying = false;
    this.verificationFailed = false;
    this.capturedImage = null;
    this.faceDetected = false;
    this.verificationErrorDetails = '';
    this.stableFaceCount = 0;
    this.citizenPhotoNotAvailableError = false;
    this.phoneNumberRequiredError = false;
    this.displayedPhoneVerificationMessage = '';
    this.showPhoneVerification = false;

    // Reset verification steps
    this.resetVerificationSteps();

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    this.detectionRunning = false;
    this.lastDetectionTime = 0;
    this.cd.detectChanges();

    await this.initializeCamera();
  }

  requestVerificationCode() {
    if (this.phoneFormControl.valid || this.phoneFormControl.disabled) {
      const phoneNumber = this.phoneFormControl.value;
      this.verificationRequestInProgress = true;

      this.parentComponent
        .submitVerification({
          selfie: this.capturedImage,
          phoneNumberKey: this.selectedPhoneKey || undefined,
          phoneNumber: this.selectedPhoneKey ? undefined : phoneNumber,
        })
        .pipe(
          finalize(() => {
            this.verificationRequestInProgress = false;
            this.cd.detectChanges();
          })
        )
        .subscribe(
          (response: any) => {
            if (response && response.status === true) {
              if (response.data?.response) {
                try {
                  const responseObj = JSON.parse(response.data.response);
                  if (
                    responseObj?.message?.includes(
                      'Face verification skip request sent successfully'
                    )
                  ) {
                    this.codeWasSent = true;
                  }
                } catch (e: any) {
                  this.verificationErrorDetails = e.toString();
                }
              }

              this.showSuccessScreen = true;
              this.cd.detectChanges();
            } else {
              const errorMessage =
                response?.responseMessage || 'Verification code request failed';
              this.verificationErrorDetails = errorMessage;
              this.toastService.show({
                body: this.translateService.instant(
                  'Request verification error!'
                ),
                type: 'error',
                delay: 5000,
              });
              this.cd.detectChanges();
            }
          },
          (error: HttpErrorResponse) => {
            const errorMessage =
              error?.error?.responseMessage ||
              'Verification code request failed. Please try again.';
            this.verificationErrorDetails = errorMessage;
            this.verificationFailed = true;
            this.toastService.show({
              body: this.translateService.instant(
                'Request verification error!'
              ),
              type: 'error',
              delay: 5000,
            });
            this.cd.detectChanges();
          }
        );
    }
  }

  closeSuccessScreen() {
    this.showSuccessScreen = false;
    this.activeModal.close({
      status: 'verification_code_requested',
      phoneNumber: this.phoneFormControl.value,
      phoneKey: this.selectedPhoneKey,
      codeWasSent: this.codeWasSent,
    });
  }

  cancel() {
    this.stopCamera();
    this.activeModal.dismiss();
  }

  ngOnDestroy() {
    this.stopCamera();
  }
}
